/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { page as SamplePage } from '../../help/sample_pages/SmartSearchPage';
import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './SmartSearch';

const SmartSearchManager: ComponentManagerFn<'SmartSearch'> = (
  platform: Platform,
): ComponentManager<'SmartSearch'> => {
  return {
    ...baseComponentManager<'SmartSearch'>(platform, 'SmartSearch'),
    isChildAllowed: (page, item, childType) =>
      (
        [
          'SmartSearchAttribute',
          'TriggerOnSearch',
          'QueryAdditionalDatasource',
          'ActionQueryAdditionalDatasource',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    defaultChildren: [],
    isItemIdReadOnly: () => true,
    props: {
      enableSavedSearch: {
        label: 'Enable Saved Search',
        type: 'boolean',
        helpText: 'If checked, searches can be saved',
        category: 'Search',
      },
      sortSearchAttributes: {
        label: 'Sort Search Attributes',
        type: 'boolean',
        helpText:
          'If checked, the search Attributes will be displayed in alphabetical order in dropdown',
        category: 'Search',
      },
      queryOwnerPublicView: {
        label: 'Disable Public View For Owner',
        type: 'boolean',
        category: 'Search',
        helpText:
          'Enable this to show "Make this view public" option for owner the view',
      },
      showPublicView: {
        label: 'Enable Public View',
        type: 'booleanExpression',
        category: 'Search',
        helpText:
          'Enable this to show "Make this view public" option in the saved search popup. Note: Owner of the view and Administrator will always be able to see "Make this view public" option',
      },
      idType: {
        category: 'Search',
        label: 'Bind To',
        type: 'select',
        options: { DataGrid: 'Data Grid', PivotTable: 'Pivot Table' },
      },
      gridId: {
        label: 'Grid Id',
        type: 'ItemID',
        helpText:
          'Control grid display , If a Grid ID is provided, the Search Attributes will use the corresponding Grid store.',
        category: 'Search',
        initialFilter: 'DataGrid',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'DataGrid' && item?.type !== 'CanvasGrid') {
            return `Item id '${value}' doesn't belong to a Grid`;
          }
          return '';
        },
      },
      pivotId: {
        label: 'Pivot Table Item Id',
        type: 'ItemID',
        category: 'Search',
        initialFilter: 'PivotTable',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'PivotTable') {
            return `Item id '${value}' doesn't belong to a Pivot Table`;
          }
          return '';
        },
      },
      searchOnBlur: {
        label: 'Enable Intermediate Search',
        type: 'boolean',
        category: 'Search',
        helpText:
          'Enable/Disable this to allow/avoid querying datsource on blur.',
      },
      border: {
        label: 'Show Border',
        type: 'select',
        options: toOptions(['full', 'bottom', 'none']),
      },
      roundedCorners: {
        label: 'Rounded Corners',
        type: 'boolean',
      },
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Search',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Search',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'roundedCorners':
          return itemData.border === 'full';
        case 'pivotId':
          return itemData.idType === 'PivotTable';
        case 'gridId':
          return itemData.idType === 'DataGrid';
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    name: 'Smart Search',
    canHaveChildren: () => true,
    category: 'Data',
    layout: 'sizeAware',
    appendOverlay: true,
    getDisplayText: (item) => item.props.gridId,
    icon: { iconName: 'search' },
    description: `**Overview**<br/>
Smart Search is an advanced, declarative query builder designed for complex data analysis. Unlike the Simple Filter (which offers a basic "contains" search), Smart Search allows users to construct structured queries using logical operators (AND/OR/NOT), nested groups, and precise comparisons (e.g., "Status is Active AND (Revenue > 5000 OR Region is North)").
It acts as a Controller component: it does not display data itself but instead takes control of a target Data Grid or Pivot Table, managing its query state, sort order, and column visibility.

**1. Integration & Binding**<br/>
Smart Search must be explicitly bound to a data visualization component to function. It operates by manipulating the underlying Data Store of the target component.
* **Bind To:** Select the type of component you are controlling (Data Grid or Pivot Table).
* **Grid ID / Pivot ID:** Select the specific Item ID of the target component.
* **Behavior:** Once bound, the Smart Search takes over the data lifecycle. Applying a filter here will trigger a reload of the grid with the new criteria.

**2. Defining Searchable Fields**<br/>
Smart Search does not automatically scan your data. You must explicitly define which columns are available for filtering by adding child components.
* **Child Component (Smart Search Attribute):** Defines a field (e.g., "OrderDate", "Status") and its data type.
* **Effect:** Adds this field to the dropdown list inside the query builder.
* **Operators:** The available operators (Equals, Contains, Greater Than, Before, After) are automatically determined by the attribute's data type.

**3. Saved Searches (Views)**<br/>
One of the component's most powerful features is the ability to persist the user's workspace state.
* **Enable Saved Search:** If checked, users can save their current query setup as a named "View".
* **What Gets Saved:** The complete filter tree (query logic) and the Grid State (column order, hidden/visible columns, sort direction).
* **Sharing (Public Views):**
    * **Enable Public View:** A boolean expression (e.g., \`$.user.role === 'Admin'\`) that determines if the current user is allowed to share their views with everyone else.
    * **Disable Public View For Owner:** Prevents the creator from accidentally modifying a public view they own, enforcing a "publish" workflow.

**4. Query Execution & UX**<br/>
* **Search On Blur:**
    * **Enabled:** The search triggers automatically when the user clicks away from the input or closes a filter chip. Best for fast, interactive exploration.
    * **Disabled:** The user must manually click the "Search" (magnifying glass) icon to apply changes. Best for very large datasets where queries are expensive.
* **Layout:** Controls the visual frame (Full, Bottom, or None). Use None or Bottom if placing the search bar inside a Card header to blend it seamlessly.

**5. Advanced Logic**<br/>
* **TriggerOnSearch:** You can add this child component to execute logic whenever a search is committed. Use this for analytics tracking or to update other parts of the dashboard.
* **Query Additional Datasource:** Allows the Smart Search to act as a central coordinator, filtering multiple Data Stores simultaneously (e.g., filtering both "Orders" and "Invoices" grids with a single date range).`,
    helpRenders: [
      {
        title: 'Basic Smart Search',
        description: `Smart Search component is used to create a search bar on the attributes selected from the provided datasource.
          
Initially there will be no filters.

Click on Add filters to start adding filters, select the attribute to filter, the operator to use and insert or select the value.

Clicking any where out will close the filter editor and show the filters already defined.

You can keep adding filters and on the end click on search icon at the right end to see the action.

You can define nested filters too, where you first choose the combiner to use ('All Of','Any Of', 'None Of'), and add filters as in the above steps to the nested filter.

You can clear filters using the clear icon on the right end.

The edit icon at the right end is to toggle editing mode to be able to add or modify or delete fiters.

You can delete a filter by clicking on the close icon on the right end of the filter.

You can modify a filter by clicking on the filter which opens the editor for the filter.

You can then save the filters defined to Saved Smart search views.

Click on the Saved Searches Menu on the left and click on Create View.

Provide a name to the view and select the options to make it default or not, public or not, whether to include Grid Filters and Grid Sort state to the View

If Save Grid Filters is checked, the Grid level filters will be saved with the view, and  Save Grid Sort behaves similarly.

Then click on save to save the view.

You can select a view to load to smart search from the list of views under the Saved Searches menu

You can modify a view by using the update menu

You can delete a view by using the delete icon on the view name in the list under the Saved Searches

A default view is a view that will be loaded when the smart search is loaded for the first time

You can also reset to the default view throught the menu if you are on a different view and need to go to the default

The Clear view menu is for clearing any views that are loaded and work with an empty view

Grid Column orders and column visibility states are also saved with views.
`,
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        visibleProps: [],
        item: {
          children: [
            'smartSearchAttribute',
            'smartSearchAttribute1',
            'smartSearchAttribute2',
            'smartSearchAttribute3',
            'smartSearchAttribute4',
            'smartSearchAttribute5',
          ],
          itemId: 'smartSearch',
          parent: 'flexItem',
          props: {},
          type: 'SmartSearch',
        },
      },
      {
        title: 'Border',
        description:
          'By default the Smart Search box will have a full border on all corners. You can choose to have bottom border or no border at all',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['border'],
        item: {
          children: [
            'smartSearchAttribute',
            'smartSearchAttribute1',
            'smartSearchAttribute2',
            'smartSearchAttribute3',
            'smartSearchAttribute4',
            'smartSearchAttribute5',
          ],
          itemId: 'smartSearch',
          parent: 'flexItem',
          props: {
            border: 'none',
          },
          type: 'SmartSearch',
        },
      },
      {
        title: 'Grid Id',
        description: `The Grid Id is needed in cases where we want to save smart search views in which case we can include the Grid Filters, Grid sort state, and column arrangment as well as visibility states to the view.

> You can either manually insert the grid id or select from the components in the tree by clicking on the search icon`,
        page: SamplePage,
        exampleId: 'e3',
        clearParentChildren: true,
        visibleProps: ['gridId'],
        item: {
          children: [
            'smartSearchAttribute',
            'smartSearchAttribute1',
            'smartSearchAttribute2',
            'smartSearchAttribute3',
            'smartSearchAttribute4',
            'smartSearchAttribute5',
          ],
          itemId: 'smartSearch',
          parent: 'flexItem',
          props: {
            border: 'none',
          },
          type: 'SmartSearch',
        },
      },
    ],
  };
};

export default SmartSearchManager;
